import { useMemo, useState } from 'react';
import { useQuery, WatchQueryFetchPolicy } from '@apollo/client';

import { useApiResponse } from '@libs/hooks';
import {
  CurrencyType,
  HistoryPeriodEnumKeys,
  MappedWalletInfo,
  RealFiatAssets,
  WalletInfoItem,
} from '@libs/types';
import { filterData } from '@libs/helpers';
import {
  getWalletInfo,
  GetWalletInfoArgs,
  getWalletInfoDetailedQuery,
  WalletInfoDetailed,
} from 'src/gql';
import { getAlternativeAssetLabel } from 'src/helpers';
import { WalletTypeEnum } from '@libs/backend-common';

type CurrencySelectDetailsAssets<T extends WalletInfoItem> = {
  fiatAssets: T[];
  cryptoAssets: T[];
  enabledCryptoAssets: T[];
  allAssets: T[];
  cryptoWithNetwork: T[];
  stableCryptoCoins: T[];
  unstableCryptoCoins: T[];
  mappedAssets: Record<CurrencyType, T>;
};

type UseWalletInfoProps = GetWalletInfoArgs & {
  pollInterval?: number;
  fetchPolicy?: WatchQueryFetchPolicy;
};
export const useWalletInfo = ({
  pollInterval,
  fetchPolicy = 'network-only',
  ...walletInfoRecord
}: UseWalletInfoProps) => {
  const [searchInputValue, setSearchInputValue] = useState('');

  const res = useQuery(getWalletInfo, {
    variables: { record: walletInfoRecord },
    fetchPolicy: fetchPolicy,
    pollInterval,
  });

  const { response, loading, errors } = useApiResponse(res);

  const walletInfoItems: MappedWalletInfo[] = useMemo(() => {
    if (!response?.getWalletInfoNew) return [];
    return response?.getWalletInfoNew.map((item) => ({
      ...item,
      codeLabel: getAlternativeAssetLabel(item.code),
    }));
  }, [response]);

  const filteredData = useMemo(() => {
    return filterData(walletInfoItems, searchInputValue, ['code', 'name']);
  }, [walletInfoItems, searchInputValue]);

  return {
    loading,
    response,
    errors,
    filteredData,
    walletInfoItems,
    searchInputValue,
    setSearchInputValue,
    refetch: res.refetch,
  };
};

export const useWalletAssetsDetailed = (
  walletType: WalletTypeEnum,
  mainAssetCode: RealFiatAssets = CurrencyType.USD,
  selectedAssetCode?: CurrencyType,
  activeDurationKey = HistoryPeriodEnumKeys.ONE_DAY,
  pollInterval?: number,
  sortByCmcRank?: boolean
) => {
  const res = useQuery(getWalletInfoDetailedQuery, {
    variables: {
      record: {
        walletType,
        assetCode: selectedAssetCode,
        valueAssetCode: mainAssetCode,
        sortByCmcRank: sortByCmcRank,
      },
      changeDetailsRecord: {
        period: activeDurationKey,
        valueAssetCode: mainAssetCode,
      },
    },
    fetchPolicy: 'network-only',
    pollInterval: pollInterval,
  });

  const { response, loading, errors } = useApiResponse(res);

  const assets = useMemo(
    () => mapAssets<WalletInfoDetailed>(response?.getWalletInfoDetailed),
    [response?.getWalletInfoDetailed]
  );

  return {
    assets,
    errors,
    loading,
    refetchAssets: res.refetch,
    walletAssets: response?.getWalletInfoDetailed,
  };
};

type MapAssetsType = WalletInfoItem;
const mapAssets = <T extends MapAssetsType>(data?: T[]) => {
  const defaultOptions: CurrencySelectDetailsAssets<T> = {
    fiatAssets: [],
    cryptoAssets: [],
    enabledCryptoAssets: [],
    allAssets: data || [],
    cryptoWithNetwork: [],
    stableCryptoCoins: [],
    unstableCryptoCoins: [],
    mappedAssets: {} as Record<CurrencyType, T>,
  };

  if (!data) return defaultOptions;

  return data.reduce((acc, item) => {
    if (item.isCrypto) {
      acc.cryptoAssets.push(item);
      if (item.isDisabled !== null && !item.isDisabled) {
        acc.enabledCryptoAssets.push(item);
        if (item.hasNetworks) {
          acc.cryptoWithNetwork?.push(item);
        }
      }
      if (item.isStable) {
        acc.stableCryptoCoins.push(item);
      } else {
        acc.unstableCryptoCoins.push(item);
      }
    } else {
      acc.fiatAssets.push(item);
    }
    acc.mappedAssets[item.code] = item;

    return acc;
  }, defaultOptions);
};
